"use client";

import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { Calendar as CalendarIcon } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

interface DatePickerProps {
  date?: Date;
  onDateChange?: (date: Date | undefined) => void;
  onBlur?: () => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

export function DatePicker({
  date,
  onDateChange,
  onBlur,
  placeholder = "Sélectionner une date",
  disabled = false,
  className,
}: DatePickerProps) {
  const handleDateChange = (selectedDate: Date | undefined) => {
    onDateChange?.(selectedDate);
    // Déclencher onBlur après sélection d'une date
    if (selectedDate) {
      onBlur?.();
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant={"outline"}
          className={cn(
            "w-full justify-start text-left font-normal h-9",
            !date && "text-muted-foreground",
            className,
          )}
          disabled={disabled}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {date ? (
            format(date, "dd/MM/yyyy", { locale: fr })
          ) : (
            <span>{placeholder}</span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={date}
          onSelect={handleDateChange}
          initialFocus
          locale={fr}
          formatters={{
            formatCaption: (date) => format(date, "MMMM yyyy", { locale: fr }),
            formatWeekdayName: (date) => format(date, "EEEEEE", { locale: fr }),
            formatMonthDropdown: (date) => format(date, "MMM", { locale: fr }),
          }}
          weekStartsOn={1}
        />
      </PopoverContent>
    </Popover>
  );
}

interface DatePickerInputProps {
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  hasError?: boolean;
}

export function DatePickerInput({
  value,
  onChange,
  onBlur,
  placeholder = "Sélectionner une date",
  disabled = false,
  className,
  hasError = false,
}: DatePickerInputProps) {
  // Convertir la valeur string en Date
  const dateValue = value && value !== "" ? new Date(value) : undefined;

  const handleDateChange = (date: Date | undefined) => {
    if (date) {
      // Convertir en format ISO string (YYYY-MM-DD) en utilisant les méthodes locales pour éviter les problèmes de fuseau horaire
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const isoString = `${year}-${month}-${day}`;
      onChange?.(isoString);
      // Déclencher onBlur immédiatement après onChange pour synchroniser la validation
      setTimeout(() => onBlur?.(), 0);
    } else {
      onChange?.("");
      setTimeout(() => onBlur?.(), 0);
    }
  };

  return (
    <DatePicker
      date={dateValue}
      onDateChange={handleDateChange}
      onBlur={onBlur}
      placeholder={placeholder}
      disabled={disabled}
      className={cn(hasError && "border-destructive", className)}
    />
  );
}
